<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useTranslation } from 'i18next-vue';
import { ArrowLeft } from 'lucide-vue-next';
import CategoryMappingEditor from '@/client/components/logged_in/category-mapping-editor.vue';
import { ADD_CATEGORY_VALUE } from '@/client/components/logged_in/category-mapping-constants';
import FeedService, { type CategoryEntry, type CategoryMappingEntry } from '@/client/service/feed';
import HelpButton from '@/client/components/common/help-button.vue';
import { useCalendarStore } from '@/client/stores/calendarStore';

const props = defineProps<{
  /** The receiving calendar's url name, from the route. */
  calendar: string;
  actorId: string;
}>();

const router = useRouter();
const { t } = useTranslation('calendars', { keyPrefix: 'category_mapping' });
const calendarStore = useCalendarStore();

// The route sync guard loads the account's calendars before this view renders,
// so a name the store does not know is not one of the account's calendars.
const calendarId = computed(() => calendarStore.getCalendarByUrlName(props.calendar)?.id ?? '');

const sourceCategories = ref<CategoryEntry[]>([]);
const localCategories = ref<CategoryEntry[]>([]);
const mappings = ref<CategoryMappingEntry[]>([]);
const loading = ref(true);
const saving = ref(false);
const saveSuccess = ref(false);
const loadError = ref('');
const saveError = ref('');

onMounted(async () => {
  if (!calendarId.value) {
    loadError.value = t('load_error');
    loading.value = false;
    return;
  }
  const feedService = new FeedService();
  try {
    const [src, own, existing] = await Promise.all([
      feedService.getSourceCategories(calendarId.value, props.actorId),
      feedService.getCalendarCategories(calendarId.value),
      feedService.getCategoryMappings(calendarId.value, props.actorId),
    ]);
    sourceCategories.value = src;
    localCategories.value = own;
    mappings.value = existing;
  }
  catch {
    loadError.value = t('load_error');
  }
  finally {
    loading.value = false;
  }
});

/**
 * Navigate back to the same calendar's Feed page.
 */
function goBack() {
  router.push({ name: 'feed', params: { calendar: props.calendar } });
}

/**
 * Save current mappings to the server.
 */
async function save() {
  saving.value = true;
  saveSuccess.value = false;
  saveError.value = '';
  try {
    const feedService = new FeedService();

    // Resolve any __add__ sentinel entries by creating new local categories
    const resolvedMappings: CategoryMappingEntry[] = await Promise.all(
      mappings.value.map(async (m) => {
        if (m.localCategoryId !== ADD_CATEGORY_VALUE) return m;
        const created = await feedService.createLocalCategory(calendarId.value, m.sourceCategoryName);
        localCategories.value = [...localCategories.value, created];
        return { ...m, localCategoryId: created.id };
      }),
    );

    await feedService.setCategoryMappings(calendarId.value, props.actorId, resolvedMappings);
    mappings.value = resolvedMappings;
    saveSuccess.value = true;
    setTimeout(() => {
      saveSuccess.value = false;
    }, 5000);
  }
  catch {
    saveError.value = t('save_error');
  }
  finally {
    saving.value = false;
  }
}
</script>

<template>
  <div
    class="category-mappings-page"
    :aria-busy="loading ? 'true' : 'false'"
  >
    <button
      type="button"
      class="back-button"
      data-testid="back-button"
      @click="goBack"
    >
      <ArrowLeft
        :size="18"
        aria-hidden="true"
      />
      {{ t('back_to_feed') }}
    </button>

    <div class="page-header">
      <div class="page-header__title-row">
        <h1>{{ t('page_title') }}</h1>
        <HelpButton />
      </div>
      <p class="subtitle">{{ t('page_subtitle') }}</p>
    </div>

    <div
      v-if="loading"
      class="loading-state"
      role="status"
      aria-live="polite"
    >
      {{ t('loading') }}
    </div>

    <div
      v-else-if="loadError"
      class="error-state"
      role="alert"
    >
      {{ loadError }}
    </div>

    <div
      v-else
      class="mappings-content"
    >
      <CategoryMappingEditor
        v-model="mappings"
        :source-categories="sourceCategories"
        :local-categories="localCategories"
      />

      <div class="save-row">
        <button
          type="button"
          class="save-button"
          :disabled="saving"
          :aria-disabled="saving ? 'true' : undefined"
          @click="save"
        >
          {{ saving ? t('saving') : t('save') }}
        </button>

        <span
          v-if="saveSuccess"
          class="save-feedback success"
          role="status"
          aria-live="polite"
        >
          {{ t('save_success') }}
        </span>
        <span
          v-if="saveError"
          class="save-feedback error"
          role="alert"
          aria-live="assertive"
        >
          {{ saveError }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.category-mappings-page {
  max-width: 42rem;
  margin: 0 auto;
  padding: var(--pav-space-4);

  @media (min-width: 640px) {
    padding: var(--pav-space-6);
  }
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: var(--pav-space-2);
  background: none;
  border: none;
  color: var(--pav-text-secondary);
  font-weight: var(--pav-font-weight-medium);
  font-size: var(--pav-font-size-small);
  cursor: pointer;
  padding: var(--pav-space-2) 0;
  margin-bottom: var(--pav-space-4);
  transition: color 0.2s;

  &:hover {
    color: var(--pav-color-interactive-active);
  }

  &:focus-visible {
    outline: 2px solid var(--pav-color-orange-500);
    outline-offset: 2px;
    border-radius: 0.25rem;
  }
}

.page-header {
  margin-bottom: var(--pav-space-6);

  &__title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  h1 {
    font-size: 1.5rem;
    font-weight: var(--pav-font-weight-bold);
    color: var(--pav-text-primary);
    margin: 0;
  }

  .subtitle {
    margin-top: var(--pav-space-1);
    font-size: 0.875rem;
    color: var(--pav-text-muted);
  }
}

.loading-state {
  padding: var(--pav-space-8);
  text-align: center;
  color: var(--pav-text-muted);
  font-style: italic;
}

.error-state {
  padding: var(--pav-space-4);
  background: var(--pav-color-alert-error-bg);
  border: 1px solid var(--pav-color-error);
  border-radius: 0.75rem;
  color: var(--pav-color-alert-error-text);
  font-size: 0.875rem;
}

.mappings-content {
  display: flex;
  flex-direction: column;
  gap: var(--pav-space-6);
  background: var(--pav-surface-primary);
  border-radius: 1rem;
  border: 1px solid var(--pav-border-secondary);
  padding: var(--pav-space-6);
}

.save-row {
  display: flex;
  align-items: center;
  gap: var(--pav-space-4);
  padding-top: var(--pav-space-4);
  border-top: 1px solid var(--pav-border-subtle);
}

.save-button {
  padding: 0.625rem 1.25rem;
  font-size: 0.875rem;
  font-weight: var(--pav-font-weight-medium);
  color: var(--pav-color-text-inverse);
  background: var(--pav-color-orange-500);
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background: var(--pav-color-orange-600);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid var(--pav-color-orange-500);
    outline-offset: 2px;
    box-shadow: 0 0 0 4px rgba(249, 115, 22, 0.2);
  }
}

.save-feedback {
  font-size: 0.875rem;
  font-weight: var(--pav-font-weight-medium);

  &.success {
    color: var(--pav-color-success);
  }

  &.error {
    color: var(--pav-color-error);
  }
}
</style>
